import { FC, UIEvent, useEffect, useReducer, useRef, useState } from 'react';
import cx from 'classnames';

import { ReactComponent as MoveDown } from 'assets/icons/move-down.svg';
import dayViewReducer, {
  initialState as dayViewInitialState,
  VIEW_ACTION,
} from 'components/DayView/reducer';
import {
  FamilyTimeSlot,
  FamilyTimeSlotBlock,
} from 'interfaces/familyBookingTypes';
import { Avatar } from 'pages/PractitionerDetailsPage/ContactInformation';
import {
  MINUTE_OF_A_BLOCK,
  convertTime,
} from 'utils/convertMinutesIntoHoursOfDay';

import SlotButton from '../SlotButton/SlotButton';
import { SlotMutation, SlotMutationPhase } from '../types';

import styles from './SlotGrid.module.scss';

interface SlotGridProps {
  contentKey: string;
  slots: FamilyTimeSlot[];
  mutation: SlotMutation;
  preferredPractitionerId?: string | null;
  isSlotSelected: (doctorId: string, block: FamilyTimeSlotBlock) => boolean;
  isSlotDisabled: (block: FamilyTimeSlotBlock) => boolean;
  onSelectSlot: (slot: FamilyTimeSlot, block: FamilyTimeSlotBlock) => void;
}

const SlotGrid: FC<SlotGridProps> = ({
  contentKey,
  slots,
  mutation,
  preferredPractitionerId,
  isSlotSelected,
  isSlotDisabled,
  onSelectSlot,
}) => {

  const practitionerHeaderRef = useRef<HTMLDivElement>(null);
  const slotGridRef = useRef<HTMLDivElement>(null);
  const [isSlotGridOverflowing, setIsSlotGridOverflowing] = useState(false);
  const [scrollState, dispatchScroll] = useReducer(
    dayViewReducer,
    dayViewInitialState
  );

  const isMutating = mutation.status !== 'idle';
  const maximumSlotCount = Math.max(0, ...slots.map((slot) => slot.blocks.length));
  const blockCount = slots.reduce((total, slot) => total + slot.blocks.length, 0);

  useEffect(() => {
    const slotGrid = slotGridRef.current;

    if (!slotGrid) return;

    const isOverflowing = slotGrid.scrollHeight > slotGrid.clientHeight;

    setIsSlotGridOverflowing(isOverflowing);
    dispatchScroll({
      type: isOverflowing ? VIEW_ACTION.SCROLL_TO_TOP : VIEW_ACTION.RESET,
    });
  }, [contentKey, slots.length, blockCount]);

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const slotGrid = event.currentTarget;

    if (practitionerHeaderRef.current) {
      practitionerHeaderRef.current.scrollLeft = slotGrid.scrollLeft;
    }

    if (!isSlotGridOverflowing) return;

    if (slotGrid.scrollTop === 0) {
      dispatchScroll({ type: VIEW_ACTION.SCROLL_TO_TOP });
    } else {
      dispatchScroll({ type: VIEW_ACTION.IS_SCROLLING });
    }

    if (
      Math.floor(slotGrid.scrollHeight - slotGrid.scrollTop) <=
      slotGrid.clientHeight
    ) {
      dispatchScroll({ type: VIEW_ACTION.SCROLL_TO_BOTTOM });
    }
  };

  const getPendingPhase = (
    slot: FamilyTimeSlot,
    block: FamilyTimeSlotBlock
  ): SlotMutationPhase | null => {
    if (mutation.status === 'idle') return null;

    const isSameDoctor = mutation.intent.doctorId === slot.doctor.id;

    const isSameBlock = mutation.intent.blockValue === block.value;

    const isSameDate = mutation.intent.date === slot.date;

    return isSameDoctor && isSameBlock && isSameDate ? mutation.status : null;
  };

  return (
    <>
      <div className={styles['practitioner-grid-header']} ref={practitionerHeaderRef}>
        {slots.map((slot) => (
          <div className={styles['practitioner-header']} key={slot.doctor.id}>
            <Avatar src={slot.doctor.avatar || ''} />

            <div className={styles['practitioner-info']}>
              <span className={styles['practitioner-name']}>
                {slot.doctor.name}
              </span>

              {slot.doctor.id === preferredPractitionerId && (
                <span className={styles['preferred']}>Preferred</span>
              )}
            </div>
          </div>
        ))}

        {scrollState.isScrolling && (
          <div className={styles['blur-bg-top']} />
        )}
      </div>

      <div
        className={styles['slot-grid']}
        onScroll={onScroll}
        ref={slotGridRef}
      >
        {slots.map((slot) => (
          <div className={styles['slot-list']} key={slot.doctor.id}>
            {Array.from({ length: maximumSlotCount }, (_, index) => {
              const block = slot.blocks[index];

              return block ? (
                <SlotButton
                  key={block.value}
                  label={convertTime(block.value * MINUTE_OF_A_BLOCK)}
                  isSelected={isSlotSelected(slot.doctor.id, block)}
                  isDisabled={isSlotDisabled(block)}
                  isLocked={isMutating}
                  pendingPhase={getPendingPhase(slot, block)}
                  onClick={() => onSelectSlot(slot, block)}
                />
              ) : (
                <span
                  className={styles['timeslot-none']}
                  key={`${slot.doctor.id}-empty-${index}`}
                >
                  -
                </span>
              );
            })}
          </div>
        ))}

        <MoveDown
          className={cx(styles['animate-icon'], {
            [styles['animate-icon-remove']]: !scrollState.displayMoreIcon,
          })}
        />
      </div>

      {scrollState.displayBlurBottom && (
        <div className={styles['blur-bg-bottom']} />
      )}
    </>
  );
};

export default SlotGrid;
